import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface CommandCenterSummary {
  readonly models: { readonly total: number; readonly active: number; readonly governancePending: number };
  readonly assistants: { readonly total: number; readonly active: number };
  readonly prompts: { readonly total: number; readonly usable: number };
  readonly requests: { readonly total: number; readonly blocked: number };
  readonly responses: { readonly total: number; readonly flagged: number };
  readonly approvals: { readonly open: number };
  readonly risks: { readonly open: number; readonly critical: number };
}

const CARDS: readonly { key: string; labelKey: string; value: (s: CommandCenterSummary) => number }[] = [
  { key: 'models-active', labelKey: 'm38.commandCenter.card.modelsActive', value: (s) => s.models.active },
  { key: 'models-pending', labelKey: 'm38.commandCenter.card.modelsPending', value: (s) => s.models.governancePending },
  { key: 'assistants-active', labelKey: 'm38.commandCenter.card.assistantsActive', value: (s) => s.assistants.active },
  { key: 'prompts-usable', labelKey: 'm38.commandCenter.card.promptsUsable', value: (s) => s.prompts.usable },
  { key: 'requests-total', labelKey: 'm38.commandCenter.card.requestsTotal', value: (s) => s.requests.total },
  { key: 'requests-blocked', labelKey: 'm38.commandCenter.card.requestsBlocked', value: (s) => s.requests.blocked },
  { key: 'responses-flagged', labelKey: 'm38.commandCenter.card.responsesFlagged', value: (s) => s.responses.flagged },
  { key: 'approvals-open', labelKey: 'm38.commandCenter.card.approvalsOpen', value: (s) => s.approvals.open },
  { key: 'risks-open', labelKey: 'm38.commandCenter.card.risksOpen', value: (s) => s.risks.open },
  { key: 'risks-critical', labelKey: 'm38.commandCenter.card.risksCritical', value: (s) => s.risks.critical },
];

/**
 * M38 — AI Command Center (§28). Read-only enterprise roll-up of the governed AI
 * estate: model registry (active + pending governance), role assistants, prompt
 * library (usable prompts), AI request/response volume (with blocked + flagged
 * counts from the §18 data-protection and §21 hallucination gates), open
 * human-in-the-loop approvals (BR-02) and the open / critical AI risk register
 * (§21). Backed by the audited command-center summary (COMMAND_CENTER_VIEW).
 * Tenant isolation is enforced server-side (LAW 4).
 */
export function AiCommandCenter(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState<CommandCenterSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: CommandCenterSummary }>(`/v1/ai/governance/command-center`, { token })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="ai-command-center">
      <h1>{t('m38.commandCenter.title')}</h1>
      <p>{t('m38.commandCenter.subtitle')}</p>
      {error && <p role="alert">{error}</p>}
      {loading || !data ? <p>{t('common.loading')}</p> : (
        <>
          <section aria-label="ai-estate-totals" className="kpi-grid">
            {CARDS.map(({ key, labelKey, value }) => (
              <div key={key} className="kpi-card" data-kpi={key}>
                <span className="kpi-label">{t(labelKey)}</span>
                <span className="kpi-value" aria-label={`total-${key}`}>{value(data)}</span>
              </div>
            ))}
          </section>

          <dl aria-label="ai-estate-breakdown">
            <dt>{t('m38.commandCenter.modelsTotal')}</dt>
            <dd aria-label="models-total">{data.models.total}</dd>
            <dt>{t('m38.commandCenter.assistantsTotal')}</dt>
            <dd aria-label="assistants-total">{data.assistants.total}</dd>
            <dt>{t('m38.commandCenter.promptsTotal')}</dt>
            <dd aria-label="prompts-total">{data.prompts.total}</dd>
            <dt>{t('m38.commandCenter.responsesTotal')}</dt>
            <dd aria-label="responses-total">{data.responses.total}</dd>
          </dl>
        </>
      )}
    </main>
  );
}
